import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStories, type StoryApi } from '../../../api/client';

export default function StoriesSection() {
  const navigate = useNavigate();
  const [stories, setStories] = useState<StoryApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getStories()
      .then(setStories)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load stories'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section className="my-8 md:my-10 rounded-[2rem] border border-stone-200/70 bg-white/80 p-6 shadow-sm">
        <div className="mb-5">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-700/80">Stories</p>
          <h2 className="mt-2 text-2xl font-bold text-stone-900" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
            African Storytelling
          </h2>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-44 rounded-3xl bg-stone-100 animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="my-8 md:my-10 rounded-[2rem] border border-stone-200/70 bg-white/80 p-6 shadow-sm">
        <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
          African Storytelling
        </h2>
        <p className="mt-3 text-sm text-amber-800/80">{error}</p>
      </section>
    );
  }

  const featuredStory = stories[0];
  const remainingStories = stories.slice(1, 4);

  return (
    <section className="my-8 md:my-10 rounded-[2rem] border border-stone-200/70 bg-white/80 p-6 shadow-sm">
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-700/80">Stories</p>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-stone-900" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
            African Storytelling
          </h2>
          <p className="mt-2 text-sm text-stone-600">
            Folklore, memory, and reflection from the motherland.
          </p>
        </div>
        {stories.length > 0 && (
          <button
            type="button"
            onClick={() => navigate('/stories')}
            className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-800"
          >
            Explore all stories
            <i className="ri-arrow-right-line" aria-hidden />
          </button>
        )}
      </div>
      {stories.length === 0 ? (
        <p className="text-stone-500 text-sm py-4">No stories yet. Check back soon.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {/* Left column — featured image */}
          {featuredStory && (
            <button
              type="button"
              onClick={() => navigate(`/stories/${featuredStory.id}`)}
              className="group overflow-hidden rounded-[1.5rem] border border-stone-200 bg-stone-950 text-left text-white shadow-sm h-full"
            >
              <div className="aspect-[4/3] w-full overflow-hidden bg-stone-900">
                <img
                  src={featuredStory.cover_image || '/story-hero-placeholder.svg'}
                  alt={featuredStory.cover_image ? '' : 'African storytelling placeholder artwork'}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                />
              </div>
            </button>
          )}

          {/* Right column — text + remaining stories */}
          <div className="flex flex-col gap-3">
            {featuredStory && (
              <button
                type="button"
                onClick={() => navigate(`/stories/${featuredStory.id}`)}
                className="group rounded-[1.5rem] border border-stone-200 bg-stone-950 p-4 text-left text-white transition hover:border-amber-500/40"
              >
                <p className="text-xs uppercase tracking-[0.22em] text-amber-200/70">Featured story</p>
                <h3 className="mt-1.5 text-base font-semibold leading-snug group-hover:text-amber-200 transition-colors">
                  {featuredStory.title}
                </h3>
                <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-stone-400">
                  {featuredStory.excerpt || 'Read the latest story from the archive.'}
                </p>
              </button>
            )}

            {remainingStories.map((story) => (
              <button
                key={story.id}
                type="button"
                onClick={() => navigate(`/stories/${story.id}`)}
                className="flex items-center gap-3 rounded-[1.25rem] border border-stone-200 bg-stone-50 p-3 text-left transition hover:border-amber-200 hover:bg-white"
              >
                {story.cover_image ? (
                  <div className="h-14 w-16 shrink-0 overflow-hidden rounded-xl bg-stone-200">
                    <img src={story.cover_image} alt="" className="h-full w-full object-cover" />
                  </div>
                ) : (
                  <div className="flex h-14 w-16 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-100 to-stone-200">
                    <i className="ri-quill-pen-line text-lg text-amber-700/60" aria-hidden />
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="line-clamp-1 text-sm font-semibold text-stone-900">{story.title}</h3>
                  <p className="mt-0.5 line-clamp-2 text-xs text-stone-500">{story.excerpt || 'No excerpt available.'}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
